import { BaseSDK, BaseEndpoint } from "@livefree/js-sdk";
import { jsx } from "react/jsx-runtime";
import React, { useMemo, useRef, useCallback, useReducer, useEffect, useContext } from "react";
const Http = new class {
  contentType = {
    JSON: "application/json",
    URL_ENCODED: "application/x-www-form-urlencoded"
  };
  urlEncodeObject(body) {
    return new URLSearchParams(
      Object.fromEntries(
        Object.entries(body).filter(([, v]) => v !== void 0)
      )
    ).toString();
  }
  jsonEncodeObject(body) {
    return JSON.stringify(body);
  }
}();
const Mode = {
  active: "active",
  passive: "passive"
};
const LocalStorageManager = new class {
  readJson(key) {
    if (key) {
      const value = window.localStorage.getItem(key);
      if (value) return JSON.parse(value);
    }
    return null;
  }
  writeJson(key, value) {
    if (value !== void 0 && value !== null) {
      window.localStorage.setItem(key, JSON.stringify(value));
    } else {
      this.remove(key);
    }
  }
  remove(key) {
    if (key) {
      window.localStorage.removeItem(key);
    }
  }
}();
const ACCESS_TOKEN_KEY = "livefreeid.idsrv.token";
const SESSION_TOKEN_KEY = "livefreeid.idsrv.session_token";
const VALIDATIONS_KEY = "livefreeid.idsrv.validators";
const TokenManager = new class {
  getToken() {
    return LocalStorageManager.readJson(ACCESS_TOKEN_KEY);
  }
  setToken(access_token) {
    if (access_token) {
      LocalStorageManager.writeJson(ACCESS_TOKEN_KEY, access_token);
    }
  }
  clearToken() {
    LocalStorageManager.remove(ACCESS_TOKEN_KEY);
  }
  getSessionToken() {
    return LocalStorageManager.readJson(SESSION_TOKEN_KEY);
  }
  setSessionToken(session_token) {
    if (session_token) {
      LocalStorageManager.writeJson(SESSION_TOKEN_KEY, session_token);
    }
  }
  clearSessionToken() {
    LocalStorageManager.remove(SESSION_TOKEN_KEY);
  }
  getValidators() {
    return LocalStorageManager.readJson(VALIDATIONS_KEY);
  }
  setValidators(validators) {
    if (!this.getValidators()) {
      LocalStorageManager.writeJson(VALIDATIONS_KEY, validators);
    }
  }
  clearValidators() {
    LocalStorageManager.remove(VALIDATIONS_KEY);
  }
  clearAll() {
    this.clearToken();
    this.clearSessionToken();
    this.clearValidators();
  }
}();
const TokenParser = new class {
  transformTokenResponse(response) {
    return {
      idToken: response.id_token ?? null,
      accessToken: response.access_token ?? null,
      expiresAt: response.expires_in ? Date.now() + response.expires_in * 1e3 : null,
      refreshToken: response.refresh_token ?? null
    };
  }
  get accessToken() {
    const token = TokenManager.getToken();
    if (!token?.accessToken) return null;
    return token.accessToken;
  }
  get idToken() {
    const token = TokenManager.getToken();
    if (!token?.idToken) return null;
    return token.idToken;
  }
  get expiresAt() {
    const token = TokenManager.getToken();
    if (!token?.expiresAt) return null;
    return token.expiresAt;
  }
  get refreshToken() {
    const token = TokenManager.getToken();
    if (!token?.refreshToken) return null;
    return token.refreshToken;
  }
  get clientId() {
    try {
      const decoded = this.decodeToken(this.accessToken);
      const cid = decoded?.client_id;
      return typeof cid === "string" ? cid : "";
    } catch {
      return "";
    }
  }
  get user() {
    try {
      const decoded = this.decodeToken(this.accessToken);
      if (!decoded) {
        return this.#fallbackUser();
      }
      return {
        id: decoded.sub,
        displayName: decoded.name,
        firstName: decoded.given_name,
        lastName: decoded.family_name,
        email: decoded.email,
        phone: decoded.phone_number,
        picture: decoded.picture
      };
    } catch {
      return this.#fallbackUser();
    }
  }
  #fallbackUser() {
    return {
      id: 0,
      displayName: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      picture: ""
    };
  }
  decodeToken(token) {
    if (!token) return null;
    const base64Url = token.split(".")[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      window.atob(base64).split("").map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2)).join("")
    );
    return JSON.parse(jsonPayload);
  }
}();
const PKCE = new class {
  async generateCode() {
    const webCrypto = this.#getWebCrypto();
    if (webCrypto) {
      const arr = new Uint8Array(32);
      webCrypto.getRandomValues(arr);
      return this.#base64Url(arr);
    }
    return void 0;
  }
  async getCodeChallenge(codeVerifier) {
    if (!codeVerifier) return null;
    const webCrypto = this.#getWebCrypto();
    if (webCrypto?.subtle) {
      return [
        "S256",
        this.#base64Url(
          await webCrypto.subtle.digest("SHA-256", this.#stringToBuffer(codeVerifier))
        )
      ];
    }
    return void 0;
  }
  #getWebCrypto() {
    if (typeof window !== "undefined" && window.crypto) {
      return window.crypto;
    }
    return null;
  }
  #stringToBuffer(input) {
    const buf = new Uint8Array(input.length);
    for (let i = 0; i < input.length; i++) {
      buf[i] = input.charCodeAt(i) & 255;
    }
    return buf;
  }
  #base64Url(buf) {
    const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
    return btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  }
}();
class QueryParams {
  #params;
  constructor(url) {
    const href = !url ? typeof window !== "undefined" ? window.location.href : "" : typeof url === "string" ? url : "href" in url ? url.href : url.href;
    this.#params = new URL(href).searchParams;
  }
  get(key) {
    return this.#params.get(key);
  }
  get codeExchangeParams() {
    return {
      error: this.#params.has("error") ? this.#params.get("error") : null,
      code: this.#params.has("code") ? this.#params.get("code") : null,
      scope: this.#params.has("scope") ? this.#params.get("scope") : null,
      state: this.#params.has("state") ? this.#params.get("state") : null,
      session_state: this.#params.has("session_state") ? this.#params.get("session_state") : null
    };
  }
  clearExchangeParams() {
    if (this.#params.has("code")) this.#params.delete("code");
    if (this.#params.has("state")) this.#params.delete("state");
    if (this.#params.has("session_state")) this.#params.delete("session_state");
    if (this.#params.has("scope")) this.#params.delete("scope");
    if (typeof window !== "undefined") {
      window.history.replaceState(
        {},
        "",
        `${window.location.origin}${window.location.pathname}${this.#params.size > 0 ? `?${this.#params.toString()}` : ""}`
      );
    }
  }
}
const strings = {
  trimEnd(str, val) {
    if (typeof str !== "string") {
      throw new Error("Parameter 'str' is not of expected type: string");
    }
    if (typeof val !== "string") {
      throw new Error("Parameter 'val' is not of expected type: string");
    }
    if (str.endsWith(val)) {
      return str.slice(0, val.length * -1);
    }
    return str;
  },
  trimStart(str, val) {
    if (typeof str !== "string") {
      throw new Error("Parameter 'str' is not of expected type: string");
    }
    if (typeof val !== "string") {
      throw new Error("Parameter 'val' is not of expected type: string");
    }
    if (str.startsWith(val)) {
      return str.slice(val.length);
    }
    return str;
  },
  trim(str, val) {
    return strings.trimStart(strings.trimEnd(str, val), val);
  },
  ensureIsString(val) {
    if (typeof val !== "string") {
      throw new Error("Parameter is not of expected type: string");
    }
    return true;
  },
  isOneOf(str, expected) {
    if (!expected.includes(str)) {
      throw new Error(`Unexpected value: ${str}. Expected one of: ${expected.join(",")}`);
    }
  }
};
class RefreshError extends Error {
  reason;
  cause;
  constructor(message, options = { reason: "refresh_failed" }) {
    super(message);
    this.name = "RefreshError";
    this.reason = options.reason;
    this.cause = options.cause;
    Object.setPrototypeOf(this, RefreshError.prototype);
  }
}
class CodeExchangeError extends Error {
  cause;
  constructor(message, options = {}) {
    super(message);
    this.name = "CodeExchangeError";
    this.cause = options.cause;
    Object.setPrototypeOf(this, CodeExchangeError.prototype);
  }
}
class DiscoveryEndpoint extends BaseEndpoint {
  #baseUrl;
  #initialized = false;
  #initializing = null;
  #doc = null;
  #authorizeUrl = null;
  #tokenUrl = null;
  #userInfoUrl = null;
  #sessionFrameUrl = null;
  #signOutUrl = null;
  constructor(config) {
    super("/.well-known/openid-configuration", config);
    this.#baseUrl = config.baseUrl;
  }
  async authorizeUrl() {
    await this.#validateAndInitialize();
    if (!this.#authorizeUrl) throw new Error("authorizeUrl not initialized");
    return this.#authorizeUrl;
  }
  async tokenUrl() {
    await this.#validateAndInitialize();
    if (!this.#tokenUrl) throw new Error("tokenUrl not initialized");
    return this.#tokenUrl;
  }
  async userInfoUrl() {
    await this.#validateAndInitialize();
    if (!this.#userInfoUrl) throw new Error("userInfoUrl not initialized");
    return this.#userInfoUrl;
  }
  async sessionFrameUrl() {
    await this.#validateAndInitialize();
    if (!this.#sessionFrameUrl) throw new Error("sessionFrameUrl not initialized");
    return this.#sessionFrameUrl;
  }
  async signOutUrl() {
    await this.#validateAndInitialize();
    if (!this.#signOutUrl) throw new Error("signOutUrl not initialized");
    return this.#signOutUrl;
  }
  endpoints(endpoints) {
    this.#authorizeUrl = this.#build(endpoints?.authorizeUrl);
    this.#tokenUrl = this.#build(endpoints?.tokenUrl);
    this.#userInfoUrl = this.#build(endpoints?.userInfoUrl);
    this.#sessionFrameUrl = this.#build(endpoints?.sessionFrameUrl);
    this.#signOutUrl = this.#build(endpoints?.signOutUrl);
    this.#initialized = true;
  }
  #build(endpoint) {
    if (!endpoint) return null;
    return `${strings.trimEnd(this.#baseUrl, "/")}/${strings.trimStart(endpoint, "/")}`;
  }
  #validateAndInitialize() {
    if (!this.#initialized && this.#initializing) return this.#initializing;
    if (this.#initialized && !this.#initializing) return;
    this.#initializing = new Promise(async (resolve, reject) => {
      try {
        this.#doc = await super.get();
        if (this.#doc) {
          this.#authorizeUrl = this.#doc.authorization_endpoint ?? null;
          this.#tokenUrl = this.#doc.token_endpoint ?? null;
          this.#userInfoUrl = this.#doc.userinfo_endpoint ?? null;
          this.#sessionFrameUrl = this.#doc.check_session_iframe ?? null;
          this.#signOutUrl = this.#doc.end_session_endpoint ?? null;
        }
        this.#initialized = true;
        this.#initializing = null;
        resolve();
      } catch (err) {
        reject(err);
      }
    });
    return this.#initializing;
  }
}
class ConnectEndpoint extends BaseEndpoint {
  #baseUrl;
  constructor(config) {
    super("", config);
    this.#baseUrl = config.baseUrl;
  }
  async userInfo(endpoint) {
    const url = this.#append(endpoint);
    return await super.httpGET({ url });
  }
  async refreshToken(endpoint, clientId, refreshToken, refreshContentType, onBeforeRefresh, onReceiveRefreshToken) {
    if (!refreshToken) {
      throw new RefreshError("Missing refresh token", { reason: "no_refresh_token" });
    }
    const url = this.#append(endpoint);
    let body = {
      grant_type: "refresh_token",
      client_id: clientId,
      refresh_token: refreshToken
    };
    body = onBeforeRefresh ? onBeforeRefresh(body) : body;
    const contentType = refreshContentType ?? Http.contentType.URL_ENCODED;
    let requestBody = body;
    if (contentType !== Http.contentType.JSON) {
      requestBody = Http.urlEncodeObject(body);
    }
    const response = await super.httpPOST({
      url,
      body: requestBody,
      options: { contentType }
    });
    if (response.error) {
      TokenManager.clearAll();
      TokenManager.clearValidators();
      throw new Error(response.error);
    }
    const transformed = onReceiveRefreshToken ? onReceiveRefreshToken(response) : response;
    TokenManager.setToken(
      TokenParser.transformTokenResponse(transformed)
    );
  }
  async exchangeCode(responseUrl, redirectUri, tokenUrl, clientId, { pkce, state }) {
    return new Promise(async (resolve, reject) => {
      const { code, session_state } = this.#validateAuthorizeResponse(responseUrl, state ?? null);
      const request = {
        client_id: clientId,
        grant_type: "authorization_code",
        code,
        redirect_uri: strings.trimEnd(redirectUri.trim(), "/")
      };
      if (pkce) {
        request.code_verifier = pkce;
      }
      const url = this.#append(tokenUrl);
      const response = await super.httpPOST({
        url,
        body: Http.urlEncodeObject(request),
        options: { contentType: Http.contentType.URL_ENCODED }
      });
      if (response.error) {
        TokenManager.clearAll();
        reject(new Error(response.error));
      } else {
        new QueryParams(responseUrl).clearExchangeParams();
        TokenManager.setToken(TokenParser.transformTokenResponse(response));
        TokenManager.setSessionToken(session_state ?? void 0);
        resolve();
      }
    });
  }
  #validateAuthorizeResponse(url, expectedState) {
    const params = new QueryParams(url);
    const { error, code, state, session_state } = params.codeExchangeParams;
    if (error) {
      throw new Error(
        `${params.get("error_description") ?? "Error exchanging code for token"} : ${params.get("error")}`
      );
    }
    if (!code) {
      throw new Error("Missing expected code in response url");
    }
    if (expectedState && (!state || state !== expectedState)) {
      if (typeof window !== "undefined") {
        window.location.href = window.location.origin;
      }
    }
    return { code, session_state };
  }
  #append(endpoint) {
    return `${strings.trimEnd(this.#baseUrl.trim(), "/")}/${strings.trimStart(endpoint.trim(), "/")}`;
  }
}
class SessionPolling {
  #ID = "check-session-iframe";
  #sessionPolling = null;
  #sessionPollingTimer = null;
  #sessionListener = null;
  async startSessionPolling(sessionUrl, { onSignedOutRemotely }) {
    if (this.#sessionPolling) return this.#sessionPolling;
    this.#sessionPolling = new Promise((resolve) => {
      const existing = typeof document !== "undefined" ? document.getElementById(this.#ID) : null;
      if (!existing && typeof document !== "undefined") {
        this.#sessionPollingTimer = setInterval(() => {
          const sessionToken = TokenManager.getSessionToken();
          if (sessionToken) {
            let sessionFrame = document.getElementById(this.#ID);
            if (!sessionFrame) {
              sessionFrame = document.createElement("iframe");
              sessionFrame.id = this.#ID;
              sessionFrame.title = this.#ID;
              sessionFrame.src = sessionUrl;
              document.body.appendChild(sessionFrame);
              this.#sessionListener = (message) => {
                if (message.source === sessionFrame?.contentWindow) {
                  if (message.data !== "unchanged") {
                    TokenManager.clearAll();
                    this.stopSessionPolling();
                    onSignedOutRemotely?.();
                  }
                }
              };
              window.addEventListener("message", this.#sessionListener);
            }
            sessionFrame.contentWindow?.postMessage(
              `${TokenParser.clientId} ${sessionToken}`
            );
          }
        }, 30 * 1e3);
      }
      resolve();
    });
    return this.#sessionPolling;
  }
  stopSessionPolling() {
    if (this.#sessionPollingTimer) {
      clearInterval(this.#sessionPollingTimer);
      this.#sessionPollingTimer = null;
    }
    if (this.#sessionListener) {
      window.removeEventListener("message", this.#sessionListener);
      this.#sessionListener = null;
    }
    if (typeof document !== "undefined") {
      const sessionFrame = document.getElementById(this.#ID);
      if (sessionFrame?.parentNode) {
        document.body.removeChild(sessionFrame);
      }
    }
  }
}
const DEFAULT_REFRESH_RETRY_DELAY_MS = 2e3;
function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
class RefreshPolling {
  #connect;
  #refreshPolling = null;
  #refreshPollingTimer = null;
  constructor(connect) {
    this.#connect = connect;
  }
  async startRefreshPolling(tokenUrl, every, threshold, checkOnStart, refreshContentType, onBeforeRefresh, onReceiveRefreshToken, onRefreshError, refreshRetryDelayMs) {
    if (this.#refreshPolling) return this.#refreshPolling;
    this.#refreshPolling = new Promise((resolve) => {
      const checkForRefresh = async () => {
        const now = Date.now();
        const expiresAt = TokenParser.expiresAt;
        const needRefresh = expiresAt != null && (expiresAt < now || expiresAt <= now + threshold);
        if (!needRefresh) return;
        if (!TokenParser.refreshToken) {
          onRefreshError?.(new RefreshError("No refresh token", { reason: "no_refresh_token" }));
          return;
        }
        const doRefresh = () => this.#connect.refreshToken(
          tokenUrl,
          TokenParser.clientId,
          TokenParser.refreshToken,
          refreshContentType,
          onBeforeRefresh,
          onReceiveRefreshToken
        );
        try {
          await doRefresh();
        } catch (firstErr) {
          await delay(refreshRetryDelayMs);
          try {
            await doRefresh();
          } catch (retryErr) {
            TokenManager.clearAll();
            TokenManager.clearValidators();
            const message = retryErr instanceof Error ? retryErr.message : String(retryErr);
            onRefreshError?.(new RefreshError(message, { reason: "refresh_failed", cause: retryErr }));
          }
        }
      };
      if (checkOnStart) void checkForRefresh();
      this.#refreshPollingTimer = setInterval(() => void checkForRefresh(), every);
      resolve();
    });
    return this.#refreshPolling;
  }
  stopRefreshPolling() {
    this.#refreshPolling = null;
    if (this.#refreshPollingTimer) {
      clearInterval(this.#refreshPollingTimer);
      this.#refreshPollingTimer = null;
    }
  }
}
class OAuth2Client extends BaseSDK {
  #discovery;
  #connect;
  #session;
  #refresh;
  constructor(authorityUrl, options = { discovery: null }) {
    super(authorityUrl);
    this.config.onRequireToken = () => Promise.resolve(TokenParser.accessToken);
    this.#discovery = new DiscoveryEndpoint(this.config);
    this.#connect = new ConnectEndpoint(this.config);
    this.#session = new SessionPolling();
    this.#refresh = new RefreshPolling(this.#connect);
    if (options.discovery) {
      this.#discovery.endpoints(options.discovery);
    }
  }
  async discovery() {
    return this.#discovery;
  }
  async userInfo() {
    const endpoint = await this.#discovery.userInfoUrl();
    try {
      return await this.#connect.userInfo(new URL(endpoint).pathname);
    } catch {
      return null;
    }
  }
  async refreshToken() {
    const tokenUrl = await this.#discovery.tokenUrl();
    if (tokenUrl) {
      return await this.#connect.refreshToken(
        new URL(tokenUrl).pathname,
        TokenParser.clientId,
        TokenParser.refreshToken
      );
    }
    return Promise.reject(new Error("Missing tokenUrl"));
  }
  async authorizationCodeRedirectUrl({
    redirectUri,
    clientId,
    state = null,
    pkce = null,
    scope = null
  }) {
    const authorizationUrl = await this.#discovery.authorizeUrl();
    const codeVerifier = await PKCE.getCodeChallenge(pkce);
    if (authorizationUrl) {
      const query = {
        client_id: clientId,
        response_type: "code",
        redirect_uri: strings.trimEnd(redirectUri.trim(), "/"),
        code_challenge_method: codeVerifier?.[0] ?? "",
        code_challenge: codeVerifier?.[1] ?? ""
      };
      if (state) {
        query.state = state;
      }
      if (scope) {
        query.scope = Array.isArray(scope) ? scope.join(" ") : scope;
      }
      return `${authorizationUrl}?${Http.urlEncodeObject(query)}`;
    }
    return "";
  }
  async signOutRedirectUrl(redirectUri, options = { signOutLocal: true }) {
    const endSessionUrl = await this.#discovery.signOutUrl();
    const idToken = TokenParser.idToken;
    const query = {
      id_token_hint: idToken ?? "",
      post_logout_redirect_uri: strings.trimEnd(redirectUri.trim(), "/")
    };
    if (options.signOutLocal) TokenManager.clearAll();
    return `${endSessionUrl}?${Http.urlEncodeObject(query)}`;
  }
  async exchangeCode(responseUrl, redirectUri, clientId, { pkce, state }) {
    const tokenUrl = await this.#discovery.tokenUrl();
    if (tokenUrl) {
      return await this.#connect.exchangeCode(
        responseUrl,
        redirectUri,
        new URL(tokenUrl).pathname,
        clientId,
        { pkce, state }
      );
    }
    return Promise.reject(new Error("Missing tokenUrl"));
  }
  async startSessionPolling(onSignedOutRemotely) {
    const sessionUrl = await this.#discovery.sessionFrameUrl();
    if (sessionUrl) {
      await this.#session.startSessionPolling(sessionUrl, {
        onSignedOutRemotely
      });
    }
  }
  async stopSessionPolling() {
    this.#session.stopSessionPolling();
  }
  async startRefreshPolling({
    every,
    threshold,
    checkOnStart,
    refreshContentType,
    onBeforeRefresh,
    onReceiveRefreshToken,
    onRefreshError,
    refreshRetryDelayMs
  } = {}) {
    const everyMs = every ?? 5 * 60 * 1e3;
    const thresholdMs = threshold ?? 6 * 60 * 1e3;
    const tokenUrl = await this.#discovery.tokenUrl();
    const retryDelayMs = refreshRetryDelayMs ?? DEFAULT_REFRESH_RETRY_DELAY_MS;
    await this.#refresh.startRefreshPolling(
      new URL(tokenUrl).pathname,
      everyMs,
      thresholdMs,
      checkOnStart,
      refreshContentType,
      onBeforeRefresh,
      onReceiveRefreshToken,
      onRefreshError,
      retryDelayMs
    );
  }
  async stopRefreshPolling() {
    this.#refresh.stopRefreshPolling();
  }
}
const ValidationCodes = new class {
  #pkce = null;
  #state = null;
  constructor() {
    const existing = TokenManager.getValidators();
    if (existing) {
      this.#pkce = existing.pkce;
      this.#state = existing.state;
    } else {
      PKCE.generateCode().then((pkce) => {
        this.#pkce = pkce ?? null;
        this.#state = Date.now().toString();
        TokenManager.setValidators({
          pkce: this.#pkce,
          state: this.#state
        });
      });
    }
  }
  get pkce() {
    return this.#pkce;
  }
  get state() {
    return this.#state;
  }
  clear() {
    this.#pkce = null;
    this.#state = null;
    TokenManager.clearValidators();
  }
}();
class Scope {
  scopes;
  offlineAccess;
  constructor(scopes, offlineAccess) {
    this.scopes = scopes;
    this.offlineAccess = offlineAccess;
  }
  getScopeArray() {
    let parsed = (this.scopes ?? "").trim().split(/\s+/).filter(Boolean);
    if (!parsed.includes("openid")) {
      parsed = [...parsed, "openid"];
    }
    if (this.offlineAccess && !parsed.includes("offline_access")) {
      parsed = [...parsed, "offline_access"];
    }
    return parsed;
  }
}
const OAuth2Context = React.createContext(null);
let hasEstablishedSessionFromExistingTokenThisPageLoad = false;
const contextReducer = (state, action) => {
  switch (action.type) {
    case "user":
      return { ...state, user: action.payload };
    case "authenticated":
      return { ...state, isAuthenticated: action.payload };
    case "authenticating":
      return { ...state, isAuthenticating: action.payload };
    case "authenticate":
      return { ...state, authenticationRequired: action.payload };
    case "logout":
      return { ...state, signingOut: action.payload };
    default:
      return state;
  }
};
const defaultAuthorizationParams = {
  usePkce: true,
  useState: true,
  offlineAccess: true
};
const defaultSessionParams = {
  pollSessionStatus: true,
  autoRefreshToken: true,
  refreshPollInterval: 5 * 60 * 1e3,
  refreshThreshold: 6 * 60 * 1e3,
  refreshContentType: "application/x-www-form-urlencoded",
  validateExistingTokenOnLoad: true
};
const defaultEventHandlers = {
  onRefreshError: () => {
  },
  onCodeExchangeError: () => {
  },
  onSignedOutRemotely: () => {
  },
  onTransformUser: (user) => user,
  onUserAuthenticated: async () => {
  },
  onBeforeRefresh: (payload) => payload,
  onReceiveRefreshToken: (payload) => payload
};
const OAuth2Provider = ({
  authorityUrl,
  clientId,
  authorizationParams = {},
  sessionParams = {},
  handlers = {},
  discovery = null,
  mode = Mode.active,
  children
}) => {
  const mergedAuthParams = { ...defaultAuthorizationParams, ...authorizationParams };
  const mergedSessionParams = { ...defaultSessionParams, ...sessionParams };
  const mergedHandlers = { ...defaultEventHandlers, ...handlers };
  const client = useMemo(
    () => new OAuth2Client(authorityUrl, { discovery: discovery ?? null }),
    [authorityUrl, discovery]
  );
  const redirectInProgressRef = useRef(false);
  const codeExchangeStartedRef = useRef(false);
  const handleSignIn = useCallback(() => {
    const redirectUri = `${window.location.origin}${window.location.pathname}`;
    const { code } = new QueryParams(window.location).codeExchangeParams;
    if (code) {
      if (codeExchangeStartedRef.current) return;
      codeExchangeStartedRef.current = true;
      client.exchangeCode(window.location, redirectUri, clientId, {
        pkce: mergedAuthParams.usePkce ? ValidationCodes.pkce : null,
        state: mergedAuthParams.useState ? ValidationCodes.state : null
      }).then(async () => {
        const user = TokenParser.user;
        dispatch({ type: "user", payload: user });
        try {
          await Promise.resolve(mergedHandlers.onUserAuthenticated?.(user));
          dispatch({ type: "authenticated", payload: true });
          dispatch({ type: "authenticating", payload: false });
          dispatch({ type: "authenticate", payload: false });
        } catch {
          dispatch({ type: "authenticated", payload: false });
          dispatch({ type: "authenticating", payload: false });
          dispatch({ type: "authenticate", payload: false });
        }
      }).catch((err) => {
        const message = err instanceof Error ? err.message : String(err);
        mergedHandlers.onCodeExchangeError?.(new CodeExchangeError(message, { cause: err }));
      });
    } else {
      if (redirectInProgressRef.current) return;
      redirectInProgressRef.current = true;
      const scope = new Scope(mergedAuthParams.scope, mergedAuthParams.offlineAccess ?? true);
      client.authorizationCodeRedirectUrl({
        redirectUri,
        clientId,
        pkce: mergedAuthParams.usePkce ? ValidationCodes.pkce ?? null : null,
        scope: scope.getScopeArray(),
        state: mergedAuthParams.useState ? ValidationCodes.state ?? null : null
      }).then((redirect) => {
        if (redirect) window.location.href = redirect;
      }).catch((err) => {
        redirectInProgressRef.current = false;
        console.error(err);
      });
    }
  }, [
    clientId,
    client,
    mergedAuthParams.scope,
    mergedAuthParams.offlineAccess,
    mergedAuthParams.usePkce,
    mergedAuthParams.useState,
    mergedHandlers
  ]);
  const handleSignOut = useCallback(
    (redirectUri = window.location.origin) => {
      dispatch({ type: "logout", payload: true });
      client.signOutRedirectUrl(redirectUri).then((redirect) => {
        window.location.href = redirect;
      }).catch((err) => console.error(err));
    },
    [client]
  );
  const handleTransformUser = useCallback(
    (user) => mergedHandlers.onTransformUser?.(user) ?? user,
    [mergedHandlers]
  );
  const [context, dispatch] = useReducer(contextReducer, {
    user: null,
    isAuthenticated: false,
    isAuthenticating: true,
    authenticationRequired: false,
    signingOut: false,
    signIn: handleSignIn,
    signOut: handleSignOut,
    transformUser: handleTransformUser
  });
  useEffect(() => {
    if (mode === Mode.active) {
      if (!TokenParser.accessToken) {
        dispatch({ type: "authenticated", payload: false });
        dispatch({ type: "authenticating", payload: true });
        dispatch({ type: "authenticate", payload: true });
      }
      if (TokenParser.accessToken && TokenParser.expiresAt && TokenParser.expiresAt <= Date.now()) {
        dispatch({ type: "authenticated", payload: false });
        dispatch({ type: "authenticating", payload: true });
        dispatch({ type: "authenticate", payload: true });
      }
      if (TokenParser.accessToken && TokenParser.expiresAt && TokenParser.expiresAt > Date.now()) {
        const runOnUserAuthenticatedOnce = async (u) => {
          if (hasEstablishedSessionFromExistingTokenThisPageLoad) {
            dispatch({ type: "user", payload: u });
            dispatch({ type: "authenticated", payload: true });
            dispatch({ type: "authenticating", payload: false });
            dispatch({ type: "authenticate", payload: false });
            return;
          }
          hasEstablishedSessionFromExistingTokenThisPageLoad = true;
          dispatch({ type: "user", payload: u });
          try {
            await Promise.resolve(mergedHandlers.onUserAuthenticated?.(u));
            dispatch({ type: "authenticated", payload: true });
            dispatch({ type: "authenticating", payload: false });
            dispatch({ type: "authenticate", payload: false });
          } catch {
            hasEstablishedSessionFromExistingTokenThisPageLoad = false;
            dispatch({ type: "authenticated", payload: false });
            dispatch({ type: "authenticating", payload: false });
            dispatch({ type: "authenticate", payload: false });
          }
        };
        if (mergedSessionParams.validateExistingTokenOnLoad) {
          client.userInfo().then(async (user) => {
            if (user) {
              const u = TokenParser.user;
              await runOnUserAuthenticatedOnce(u);
            } else {
              dispatch({ type: "authenticated", payload: false });
              dispatch({ type: "authenticating", payload: true });
              dispatch({ type: "authenticate", payload: true });
            }
          });
        } else {
          const u = TokenParser.user;
          void runOnUserAuthenticatedOnce(u);
        }
      }
    }
    if (mode === Mode.passive) {
      if (TokenParser.accessToken && TokenParser.expiresAt && TokenParser.expiresAt > Date.now()) {
        dispatch({ type: "user", payload: TokenParser.user });
        dispatch({ type: "authenticated", payload: true });
        dispatch({ type: "authenticating", payload: false });
        dispatch({ type: "authenticate", payload: false });
      }
    }
  }, [authorityUrl, client, mergedSessionParams.validateExistingTokenOnLoad, mode]);
  useEffect(() => {
    if (mode === Mode.active && mergedSessionParams.autoRefreshToken) {
      client.startRefreshPolling({
        every: mergedSessionParams.refreshPollInterval,
        threshold: mergedSessionParams.refreshThreshold,
        checkOnStart: true,
        refreshContentType: mergedSessionParams.refreshContentType,
        onBeforeRefresh: mergedHandlers.onBeforeRefresh,
        onReceiveRefreshToken: mergedHandlers.onReceiveRefreshToken,
        onRefreshError: mergedHandlers.onRefreshError,
        refreshRetryDelayMs: mergedSessionParams.refreshRetryDelayMs
      });
      return () => {
        void client.stopRefreshPolling();
      };
    }
  }, [
    mergedSessionParams.autoRefreshToken,
    mergedSessionParams.refreshContentType,
    mergedSessionParams.refreshRetryDelayMs,
    mergedHandlers.onBeforeRefresh,
    mergedHandlers.onReceiveRefreshToken,
    mergedHandlers.onRefreshError,
    client,
    mode
  ]);
  useEffect(() => {
    if (mode === Mode.active && mergedSessionParams.pollSessionStatus) {
      client.startSessionPolling(mergedHandlers.onSignedOutRemotely ?? (() => {
      }));
      return () => {
        void client.stopSessionPolling();
      };
    }
  }, [mergedSessionParams.pollSessionStatus, client, mergedHandlers.onSignedOutRemotely, mode]);
  return /* @__PURE__ */ jsx(OAuth2Context.Provider, { value: context, children });
};
const useOAuth2 = () => {
  const context = useContext(OAuth2Context);
  if (context === null || context === void 0) {
    throw new Error("useOAuth2 hook requires OAuth2Provider.");
  }
  return {
    user: context.user ? context.transformUser(context.user) : context.user,
    isAuthenticated: context.isAuthenticated,
    isAuthenticating: context.isAuthenticating,
    authenticationRequired: context.authenticationRequired,
    signingOut: context.signingOut,
    signIn: context.signIn,
    signOut: context.signOut
  };
};
const useAuthenticatedUser = () => {
  const { signIn } = useOAuth2();
  try {
    return TokenParser.user;
  } catch {
    signIn();
    return void 0;
  }
};
const withUser = (WrappedComponent) => {
  const Wrapped = (props) => {
    const user = useAuthenticatedUser();
    return /* @__PURE__ */ jsx(WrappedComponent, { ...props, user });
  };
  return Wrapped;
};
export {
  CodeExchangeError,
  Http,
  Mode,
  OAuth2Client,
  OAuth2Context,
  OAuth2Provider,
  PKCE,
  QueryParams,
  RefreshError,
  Scope,
  TokenManager,
  TokenParser,
  ValidationCodes,
  useAuthenticatedUser,
  useOAuth2,
  withUser
};
//# sourceMappingURL=index.js.map
